import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import editContractorDrawerSlice from "../../../widgets/EditContractorDrawer/reducer";
import EditableContractorModel from "../../../viewModels/Contractors/ContractorModel";
import { isInternalOffice } from "../../../utils/isInternalOffice";

import qs from "qs";

import { AuthActions } from "../../../auth/actions";

import { BitrixInitOptions, BitrixOptions, DevRoleOverride, IGlobalState, MessageConfig } from "./types";

import { UserInfoModel } from "../../../viewModels/User/UserInfoModel";

import { GlobalSettingsModel } from "../../../viewModels/Settings/SettingsModel";
import { ContactFormInputModel } from "../../ContactModal/components/ContactForm/types";
import { AdditionalJobModel } from "../../../viewModels/Settings/AdditionalJobModel";
import { SupportedLanguages } from "../../../viewModels/Settings/TranslationModel";
import DisplayScope from "../../../types/DisplayScope";
import UserSettingModel from "../../../viewModels/Settings/UserSettingsModel";
import { DealBasicInfoModel } from "../../../viewModels/Deal/DealModel";
import DealNotificationModel, { DealNotificationUtils } from "../../../viewModels/Notification/DealNotificationModel";
import type { SendDealNotificationResponseFormInput } from "../../../components/DealNotificationsList/types";
import dealNotificationModalSlice from "../../../widgets/DealNotificationModal/reducer";
import { BitrixCompanyModel, ShortBitrixCompanyModel } from "../../../viewModels/Dictionaries/BitrixCompanyModel";
import { ProductUsageEventRequestDto } from "../../../api/apiServices/backOffice/controllers/productUsageEvents/productUsageEvents.requestsDto";

// TODO придумать что-то как красивее парсить данные для интиализации
function updateLocalContractor(state: IGlobalState, contractor: EditableContractorModel) {
  if (!isInternalOffice(state.userInfo?.company.settings.officeType)) return;
  const item = { id: contractor.id, name: contractor.name, isBlackList: contractor.isBlackList, creationDate: contractor.creatorDate };
  state.clients = state.clients.filter((client) => client.id !== contractor.id);
  state.carriers = state.carriers.filter((carrier) => carrier.id !== contractor.id);
  state.bitrixCarriers = state.bitrixCarriers.filter((carrier) => carrier.bitrixId !== contractor.id);
  if (contractor.contractorTypes.includes("CLIENT")) state.clients.push(item);
  if (contractor.contractorTypes.includes("CARRIER")) {
    state.carriers.push(item);
    state.bitrixCarriers.push({ bitrixId: contractor.id, name: contractor.name });
  }
  delete state.bitrixCompanyDetailsById[contractor.id];
}

const query = qs.parse(window.location.search, { ignoreQueryPrefix: true });
const initOpts: BitrixInitOptions | undefined = query.initOpts ? JSON.parse(query.initOpts as string) : undefined;

let host: "WEB" | "BITRIX" = "WEB";
let displayScope: DisplayScope = "ALL";

let bitrixOptions;
if (initOpts && initOpts.apiKey === import.meta.env.VITE_BITRIX_API_KEY) {
  host = "BITRIX";
  bitrixOptions = {
    email: initOpts.email,
    leadId: initOpts.leadId,
    companyId: initOpts.companyId,
  };

  if (initOpts.displayScope) {
    displayScope = initOpts.displayScope;
  }
}

const initialState: IGlobalState = {
  workCompanySwitchStatus: "idle",
  loading: true,
  userDataLoading: false,

  initialLoadingError: false,

  userInfo: undefined!,
  devRoleOverride: undefined,

  isAuthModalVisible: false,

  isContactModalVisible: false,
  isContactModalLoading: false,

  // глобавльные данные
  globalCurrencies: undefined!,
  transportationTypes: undefined!,
  transportCharacteristics: undefined!,
  additionalJobs: undefined!,
  conditionDeliveries: undefined!,
  documentTypes: undefined!,
  applicationStatuses: undefined!,
  countries: undefined!,
  locationTypes: undefined!,
  companyTypes: undefined!,
  userTypes: undefined!,
  applicationServices: undefined!,
  incotermsTypes: undefined!,
  nomenclatures: undefined!,
  expenseCategoryServices: undefined!,
  paymentKinds: undefined!,
  paymentTypes: undefined!,
  regions: undefined!,
  leadStatuses: undefined!,
  dangerousGoodsClasses: undefined!,
  transportationStages: undefined!,
  transportationSchemes: undefined!,
  vehicleTypes: undefined!,

  // данные зависящие от компании
  settlementAccounts: undefined!,
  currencies: undefined!,
  managers: undefined!,
  logisticians: undefined!,
  dealsBasicInfo: undefined!,
  users: undefined!,

  roleUserActions: [
    {
      id: 1,
      name: "Просмотр",
    },
    {
      id: 2,
      name: "Редактирование",
    },
    {
      id: 3,
      name: "Назначать заявки",
    },
  ],
  roleUserActionSettings: [
    {
      id: 1,
      name: "Справочник стран",
      actions: [1, 2],
    },
    {
      id: 2,
      name: "Справочник пунктов",
      actions: [1, 2],
    },
    {
      id: 3,
      name: "Справочник коэффициентов",
      actions: [1, 2],
    },
    {
      id: 4,
      name: "Справочник валют",
      actions: [1],
    },
    {
      id: 5,
      name: "Справочник внешних пользователей",
      actions: [1, 2],
    },

    {
      id: 6,
      name: "Справочник внутренних пользователей",
      actions: [1, 2],
    },
    {
      id: 7,
      name: "Справочник маршрутов",
      actions: [1, 2],
    },
    {
      id: 8,
      name: "Заявки",
      actions: [3],
    },
  ],

  messageConfigShow: undefined!,
  waypointLocations: undefined!,
  borderCrossingLocations: undefined!,
  borderCrossingPairs: [],

  host,
  bitrixOptions,
  displayScope,
  // host: "BITRIX",
  // bitrixOptions: {
  //   email: "admin@example.com",
  // },
  // displayScope: "DEALS",

  language: undefined!,

  pendingFillDeals: [],
  dealNotificationsLoadingData: false,

  bitrixCarriersLoading: false,
  clients: [],
  carriers: [],
  bitrixCarriers: [],
  regularBitrixCompanies: [],
  regularBitrixCompaniesLoading: false,
  bitrixCompanyDetailsById: {},
  bitrixCompanyDetailsLoadingById: {},
  bitrixCompanyDetailsErrorById: {},
};

const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    switchWorkCompany(state, _action: PayloadAction<number>) {
      state.workCompanySwitchStatus = "pending";
    },
    switchWorkCompanySuccess(state) {
      state.workCompanySwitchStatus = "success";
    },
    switchWorkCompanyReject(state) {
      state.workCompanySwitchStatus = "error";
    },
    initialDataLoaded(state) {
      state.loading = false;
    },
    userDataLoaded(state) {
      state.userDataLoading = false;
    },
    initialDataLoadedError(state) {
      state.initialLoadingError = true;
    },
    openAuthModal(state) {
      state.isAuthModalVisible = true;
    },
    closeAuthModal(state) {
      state.isAuthModalVisible = false;
    },
    openContactModal(state) {
      state.isContactModalVisible = true;
    },
    closeContactModal(state) {
      state.isContactModalVisible = false;
    },

    sendFeedbackForm(state, _action: PayloadAction<ContactFormInputModel>) {
      state.isContactModalLoading = true;
    },
    sendFeedbackFormSuccess(state) {
      state.isContactModalLoading = false;
      state.isContactModalVisible = false;
    },
    sendFeedbackFormError(state) {
      state.isContactModalLoading = false;
    },
    recordProductUsageEvent(_state, _action: PayloadAction<ProductUsageEventRequestDto>) {},
    recordProductUsageEventSuccess() {},
    recordProductUsageEventReject() {},
    globalSettingsLoaded(state, action: PayloadAction<GlobalSettingsModel>) {
      const {
        currencies,
        transportCharacteristics,
        transportationTypes,
        additionalJobs,
        documentTypes,
        conditionDeliveries,
        orderStatuses,
        countries,
        locationTypes,
        companyTypes,
        waypoints,
        userTypes,
        borderCrossings,
        borderCrossingPairs,
        applicationServices,
        incotermsTypes,
        nomenclatures,
        expenseCategoryServices,
        paymentKinds,
        paymentTypes,
        regions,
        leadStatuses,
        dangerousGoodsClasses,
        transportationStages,
        transportationSchemes,
        vehicleTypeList,
      } = action.payload;
      state.globalCurrencies = currencies;
      state.transportCharacteristics = transportCharacteristics;
      state.transportationTypes = transportationTypes;
      state.additionalJobs = additionalJobs;
      state.conditionDeliveries = conditionDeliveries;
      state.documentTypes = documentTypes;
      state.applicationStatuses = orderStatuses;
      state.countries = countries;
      state.locationTypes = locationTypes;
      state.companyTypes = companyTypes;
      state.waypointLocations = waypoints;
      state.userTypes = userTypes;
      state.borderCrossingLocations = borderCrossings;
      state.borderCrossingPairs = borderCrossingPairs ?? [];
      state.applicationServices = applicationServices;
      state.incotermsTypes = incotermsTypes;
      state.nomenclatures = nomenclatures;
      state.expenseCategoryServices = expenseCategoryServices;
      state.paymentKinds = paymentKinds;
      state.paymentTypes = paymentTypes;
      state.regions = regions;
      state.leadStatuses = leadStatuses;
      state.dangerousGoodsClasses = dangerousGoodsClasses;
      state.transportationStages = transportationStages;
      state.transportationSchemes = transportationSchemes;
      state.vehicleTypes = vehicleTypeList;
    },
    userSettingsLoaded(state, action: PayloadAction<{ settings: UserSettingModel }>) {
      const { settlementAccounts, currencies, deals, users } = action.payload.settings;

      const managers = users.filter(
        (x) =>
          x.role?.userType === "MANAGER" ||
          x.role?.userType === "ADMINISTRATOR" ||
          x.role?.userType === "MANAGER_LOGISTICIAN" ||
          x.role?.userType === "ACCOUNTANT",
      );
      const logisticians = users.filter(
        (x) =>
          x.role?.userType === "LOGISTICIAN" ||
          x.role?.userType === "ADMINISTRATOR" ||
          x.role?.userType === "MANAGER_LOGISTICIAN" ||
          x.role?.userType === "ACCOUNTANT",
      );
      state.settlementAccounts = settlementAccounts;
      state.currencies = currencies;
      state.managers = managers;
      state.logisticians = logisticians;
      state.dealsBasicInfo = deals;
      state.users = users;
      state.clients = action.payload.settings.clients;
      state.carriers = action.payload.settings.carriers;
    },
    userLoaded(state, action: PayloadAction<UserInfoModel>) {
      state.userInfo = action.payload;
    },
    setDevRoleOverride(state, action: PayloadAction<DevRoleOverride | undefined>) {
      state.devRoleOverride = action.payload;
    },
    setMessageConfigShow(state, action: PayloadAction<MessageConfig>) {
      state.messageConfigShow = { ...action.payload };
    },
    changeContainerIssuingAdditionalJob(state, action: PayloadAction<AdditionalJobModel>) {
      const index = state.additionalJobs.findIndex((x) => x.id === action.payload.id);
      state.additionalJobs[index] = action.payload;
    },
    setBitrixOptions(state, action: PayloadAction<BitrixOptions | undefined>) {
      state.bitrixOptions = action.payload;
      if (action.payload) {
        state.host = "BITRIX";
      }
    },
    setLanguage(state, action: PayloadAction<SupportedLanguages>) {
      state.language = action.payload;
    },

    setPendingFillDeals(state, action: PayloadAction<Array<DealBasicInfoModel>>) {
      state.pendingFillDeals = action.payload;
    },

    setDealsNotificationsToUs(state, action: PayloadAction<Array<DealNotificationModel>>) {
      state.dealNotitficationsToUs = action.payload;
    },

    setDealsNotificationsByUs(
      state,
      action: PayloadAction<{ requests: Array<DealNotificationModel>; responses: Array<DealNotificationModel> }>,
    ) {
      state.dealNotitficationsByUs = action.payload;
    },

    sendDealNotificationResponse(state, _action: PayloadAction<SendDealNotificationResponseFormInput>) {
      state.dealNotificationsLoadingData = true;
    },
    sendDealNotificationResponseSuccess(state, action: PayloadAction<DealNotificationModel>) {
      state.dealNotificationsLoadingData = false;
      if (state.dealNotitficationsToUs) {
        state.dealNotitficationsToUs = state.dealNotitficationsToUs.filter((x) => !DealNotificationUtils.isSameQuestion(x, action.payload));
      }
      if (state.dealNotitficationsByUs && action.payload.requester.id === state.userInfo.id) {
        const requestIndex = state.dealNotitficationsByUs.requests.findIndex((x) =>
          DealNotificationUtils.isSameQuestion(x, action.payload),
        );
        if (requestIndex !== -1) {
          state.dealNotitficationsByUs.requests.splice(requestIndex, 1);
        }

        state.dealNotitficationsByUs.responses.unshift(action.payload);
      }
    },
    sendDealNotificationResponseReject(state) {
      state.dealNotificationsLoadingData = false;
    },

    closeDealNotification(state, _action: PayloadAction<Pick<DealNotificationModel, "id" | "officeType">>) {
      state.dealNotificationsLoadingData = true;
    },
    closeDealNotificationSuccess(state, action: PayloadAction<DealNotificationModel>) {
      state.dealNotificationsLoadingData = false;
      if (state.dealNotitficationsByUs) {
        state.dealNotitficationsByUs = {
          ...state.dealNotitficationsByUs,
          responses: state.dealNotitficationsByUs.responses.filter((x) => !DealNotificationUtils.isSameQuestion(x, action.payload)),
        };
      }
    },
    closeDealNotificationReject(state) {
      state.dealNotificationsLoadingData = false;
    },

    loadRegularBitrixCompanies(state) {
      state.regularBitrixCompanies = [];
      state.regularBitrixCompaniesLoading = true;
    },
    loadRegularBitrixCompaniesSuccess(state, action: PayloadAction<Array<BitrixCompanyModel>>) {
      state.regularBitrixCompanies = action.payload;
      state.regularBitrixCompaniesLoading = false;
    },
    loadRegularBitrixCompaniesReject(state) {
      state.regularBitrixCompaniesLoading = false;
    },
    loadBitrixCarriersSucces(state, action: PayloadAction<Array<ShortBitrixCompanyModel>>) {
      state.bitrixCarriers = action.payload;
      state.bitrixCarriersLoading = false;
    },
    loadBitrixCarriersReject(state) {
      state.bitrixCarriersLoading = false;
    },
    loadBitrixCompanyDetails(state, action: PayloadAction<number>) {
      if (state.bitrixCompanyDetailsById[action.payload]) {
        state.bitrixCompanyDetailsLoadingById[action.payload] = false;
        state.bitrixCompanyDetailsErrorById[action.payload] = false;
        return;
      }

      state.bitrixCompanyDetailsLoadingById[action.payload] = true;
      state.bitrixCompanyDetailsErrorById[action.payload] = false;
    },
    loadBitrixCompanyDetailsSuccess(state, action: PayloadAction<{ id: number; company: BitrixCompanyModel }>) {
      state.bitrixCompanyDetailsById[action.payload.id] = action.payload.company;
      state.bitrixCompanyDetailsLoadingById[action.payload.id] = false;
      state.bitrixCompanyDetailsErrorById[action.payload.id] = false;
    },
    loadBitrixCompanyDetailsReject(state, action: PayloadAction<number>) {
      state.bitrixCompanyDetailsLoadingById[action.payload] = false;
      state.bitrixCompanyDetailsErrorById[action.payload] = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(editContractorDrawerSlice.actions.createContractorSuccess, (state, action) => {
      updateLocalContractor(state, action.payload);
    });
    builder.addCase(editContractorDrawerSlice.actions.updateContractorSuccess, (state, action) => {
      updateLocalContractor(state, action.payload);
    });
    builder.addCase(AuthActions.login, (state) => {
      state.userDataLoading = true;
    });
    builder.addCase(AuthActions.loginSuccess, (state, action) => {
      state.userInfo = action.payload;
      state.isAuthModalVisible = false;
    });
    builder.addCase(AuthActions.loginError, (state) => {
      state.userDataLoading = false;
    });
    builder.addCase(AuthActions.logoutSuccess, (state) => {
      state.workCompanySwitchStatus = "idle";
      state.userInfo = undefined!;
      state.devRoleOverride = undefined;
      state.regularBitrixCompanies = [];
      state.clients = [];
      state.carriers = [];
      state.bitrixCarriers = [];
      state.bitrixCompanyDetailsById = {};
      state.regularBitrixCompaniesLoading = false;
    });
    builder.addCase(dealNotificationModalSlice.actions.createDealNotificationSuccess, (state, action) => {
      if (state.dealNotitficationsToUs) {
        if (DealNotificationUtils.isRequestRecipient(action.payload, state.userInfo.id)) {
          state.dealNotitficationsToUs.unshift(action.payload);
        }
      }
      if (state.dealNotitficationsByUs) {
        state.dealNotitficationsByUs.requests.unshift(action.payload);
      }
    });
  },
});

export default globalSlice;
